import { Helmet } from 'react-helmet-async';
import { useState, useEffect, type ChangeEvent, type MouseEvent, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormGroup,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import Iconify from '../../components/iconify';
import { BreadCrumbs } from '../../components/nav-section';
import { SortableList } from '../../components/sortableList';
import { useToast } from '../../hooks/useToast';
import type { QuestionProps, QuestionOption } from '../../models';
import { useGetSectionWithQuestionsQuery, useUpdateSectionMutation } from '../../slices/apis/sectionApi';
import {
	useCreateQuestionMutation,
	useUpdateQuestionMutation,
	useDeleteQuestionMutation,
	useReorderQuestionsMutation,
} from '../../slices/apis/questionnaireMgmtApi';

type QuestionType = 'TEXT' | 'YES_NO' | 'MULTIPLE_CHOICE' | 'CONSENT';
type SelectionType = 'SINGLE' | 'MULTI';

interface QuestionForm {
	text: string;
	type: QuestionType;
	isRequired: boolean;
	selectionType: SelectionType;
	options: Array<{ label: string; exclusive: boolean }>;
	consentContent: string;
}

const emptyForm = (): QuestionForm => ({
	text: '',
	type: 'TEXT',
	isRequired: false,
	selectionType: 'SINGLE',
	options: [{ label: '', exclusive: false }],
	consentContent: '',
});

const fromQuestion = (q: QuestionProps): QuestionForm => ({
	text: q.text,
	type: q.type as QuestionType,
	isRequired: q.isRequired,
	selectionType: (q.selectionType as SelectionType) ?? 'SINGLE',
	options: q.options?.length
		? q.options.map((o) => ({ label: o.label, exclusive: !!o.exclusive }))
		: [{ label: '', exclusive: false }],
	consentContent: q.consentContent ?? '',
});

const TYPE_LABELS: Record<QuestionType, string> = {
	TEXT: 'Text',
	YES_NO: 'Yes / No',
	MULTIPLE_CHOICE: 'Multiple Choice',
	CONSENT: 'Consent',
};

const TYPE_CHIP_COLOR: Record<QuestionType, 'default' | 'primary' | 'secondary' | 'info' | 'warning'> = {
	TEXT: 'default',
	YES_NO: 'info',
	MULTIPLE_CHOICE: 'secondary',
	CONSENT: 'warning',
};

export default function SectionEdit() {
	const { sectionId } = useParams() as { sectionId: string };
	const navigate = useNavigate();
	const { data, isLoading } = useGetSectionWithQuestionsQuery(Number(sectionId));
	const [updateSection, { isLoading: isUpdating }] = useUpdateSectionMutation();

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const { showToast, ToastElement } = useToast();

	const handleNavigate = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			navigate(event.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	useEffect(() => {
		if (data) {
			setName(data.name);
			setDescription(data.description ?? '');
		}
	}, [data]);

	const handleSave = async () => {
		if (!name.trim()) {
			showToast('Name is required.', 'error');
			return;
		}

		try {
			await updateSection({ id: Number(sectionId), name, description: description || null });

			if (localQuestions.length > 0) {
				await reorderQuestions({
					questions: localQuestions.map((q, i) => ({ id: q.id, orderIndex: i + 1 })),
				}).unwrap();
			}

			showToast('Section has been updated successfully!', 'success');
		} catch {
			showToast('Failed to save section. Please try again.', 'error');
		}
	};

	const [localQuestions, setLocalQuestions] = useState<QuestionProps[]>([]);
	const [reorderQuestions, { isLoading: isReordering }] = useReorderQuestionsMutation();

	useEffect(() => {
		if (data?.questions) {
			setLocalQuestions([...data.questions].sort((a, b) => a.orderIndex - b.orderIndex));
		}
	}, [data?.questions]);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingQuestion, setEditingQuestion] = useState<QuestionProps | null>(null);
	const [form, setForm] = useState<QuestionForm>(emptyForm());
	const [dialogError, setDialogError] = useState<string | null>(null);

	const [createQuestion, { isLoading: isCreatingQ }] = useCreateQuestionMutation();
	const [updateQuestion, { isLoading: isUpdatingQ }] = useUpdateQuestionMutation();
	const [deleteQuestion] = useDeleteQuestionMutation();

	const openAddDialog = () => {
		setEditingQuestion(null);
		setForm(emptyForm());
		setDialogError(null);
		setDialogOpen(true);
	};

	const openEditDialog = (q: QuestionProps) => {
		setEditingQuestion(q);
		setForm(fromQuestion(q));
		setDialogError(null);
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
	};

	const handleFormChange = (field: keyof QuestionForm, value: unknown) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleOptionChange = (index: number, field: 'label' | 'exclusive', value: string | boolean) => {
		setForm((prev) => {
			const options = [...prev.options];
			options[index] = { ...options[index], [field]: value };
			return { ...prev, options };
		});
	};

	const addOption = () => {
		setForm((prev) => ({ ...prev, options: [...prev.options, { label: '', exclusive: false }] }));
	};

	const removeOption = (index: number) => {
		setForm((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
	};

	const handleSaveQuestion = async () => {
		setDialogError(null);
		if (!form.text.trim()) {
			setDialogError('Question text is required');
			return;
		}
		if (form.type === 'MULTIPLE_CHOICE') {
			const valid = form.options.every((o) => o.label.trim());
			if (!valid) {
				setDialogError('All option labels are required');
				return;
			}
			if (form.options.length === 0) {
				setDialogError('At least one option is required');
				return;
			}
		}
		if (form.type === 'CONSENT' && !form.consentContent.trim()) {
			setDialogError('Consent content is required');
			return;
		}

		const nextOrder = localQuestions.length + 1;
		const payload = {
			text: form.text,
			type: form.type,
			isRequired: form.type === 'CONSENT' ? false : form.isRequired,
			sectionId: Number(sectionId),
			orderIndex: editingQuestion ? editingQuestion.orderIndex : nextOrder,
			selectionType: form.type === 'MULTIPLE_CHOICE' ? form.selectionType : null,
			options:
				form.type === 'MULTIPLE_CHOICE'
					? form.options.map((o): QuestionOption => ({ label: o.label, ...(o.exclusive ? { exclusive: true } : {}) }))
					: null,
			consentContent: form.type === 'CONSENT' ? form.consentContent : null,
		};

		try {
			if (editingQuestion) {
				await updateQuestion({ id: editingQuestion.id, ...payload }).unwrap();
				showToast('Question has been updated successfully!', 'success');
			} else {
				await createQuestion(payload).unwrap();
				showToast('Question has been added successfully!', 'success');
			}
			setDialogOpen(false);
		} catch {
			setDialogError('Failed to save question. Please try again.');
		}
	};

	const handleDeleteQuestion = async (id: number) => {
		try {
			await deleteQuestion(id).unwrap();
			showToast('Question has been removed successfully!', 'success');
		} catch {
			showToast('Failed to remove question.', 'error');
		}
	};

	if (isLoading) {
		return (
			<Container maxWidth="md" sx={{ mt: 4 }}>
				<LinearProgress />
			</Container>
		);
	}

	const isSavingSection = isUpdating || isReordering;
	const isSavingQuestion = isCreatingQ || isUpdatingQ;
	const breadCrumbItems = [
		{
			text: 'My Sections',
			onClick: handleNavigate,
			dataRoute: '/dashboard/admin/sections',
		},
		{
			text: `Section #${sectionId}`,
		},
	];

	return (
		<>
			<Helmet>
				<title>Edit Section — {process.env.REACT_APP_ORG}</title>
			</Helmet>
			{ToastElement}
			<Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
				<Card variant="outlined">
					<CardContent>
						<Stack spacing={3}>
							<Box>
								<BreadCrumbs items={breadCrumbItems} />
							</Box>

							<Stack spacing={2}>
								<TextField
									label="Name"
									variant="standard"
									required
									fullWidth
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
								<TextField
									label="Description"
									variant="standard"
									fullWidth
									multiline
									value={description}
									onChange={(e) => setDescription(e.target.value)}
								/>
							</Stack>

							<Box>
								<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
									<Typography variant="h6">Questions</Typography>
									<Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAddDialog}>
										Add Question
									</Button>
								</Stack>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Drag the handle to reorder questions. Changes will be saved when you click Save.
								</Typography>
								<Divider sx={{ mb: 2 }} />

								{localQuestions.length === 0 ? (
									<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
										No questions yet. Click <strong>Add Question</strong> to get started.
									</Typography>
								) : (
									<SortableList
										items={localQuestions}
										onChange={setLocalQuestions}
										renderItem={(q) => (
											<SortableList.Item id={q.id}>
												<Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
													<Box sx={{ flexGrow: 1, minWidth: 0 }}>
														<Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
															{q.text}
														</Typography>
														<Stack direction="row" spacing={0.5} mt={0.5}>
															<Chip
																label={TYPE_LABELS[q.type as QuestionType]}
																color={TYPE_CHIP_COLOR[q.type as QuestionType]}
																size="small"
															/>
															{q.type !== 'CONSENT' && (
																<Chip label={q.isRequired ? 'Required' : 'Optional'} size="small" variant="outlined" />
															)}
															{q.type === 'MULTIPLE_CHOICE' && q.selectionType && (
																<Chip label={q.selectionType} size="small" variant="outlined" />
															)}
														</Stack>
													</Box>
													<Tooltip title="Edit">
														<IconButton size="small" onClick={() => openEditDialog(q)}>
															<EditIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete">
														<IconButton size="small" color="error" onClick={() => handleDeleteQuestion(q.id)}>
															<Iconify icon="eva:trash-2-outline" width={18} />
														</IconButton>
													</Tooltip>
												</Stack>
												<SortableList.DragHandle />
											</SortableList.Item>
										)}
									/>
								)}
							</Box>

							<Stack direction="row" spacing={4}>
								<Button
									loading={isSavingSection}
									variant="outlined"
									color="primary"
									onClick={handleSave}
								>
									<span>Save</span>
								</Button>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			</Container>

			{/* Question add/edit dialog */}
			<Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
				<DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						{dialogError && (
							<Typography variant="body2" color="error">
								{dialogError}
							</Typography>
						)}

						<TextField
							label="Question Text"
							required
							fullWidth
							multiline
							minRows={2}
							value={form.text}
							onChange={(e) => handleFormChange('text', e.target.value)}
						/>

						<FormControl fullWidth size="small">
							<InputLabel>Type</InputLabel>
							<Select value={form.type} label="Type" onChange={(e) => handleFormChange('type', e.target.value)}>
								{(Object.keys(TYPE_LABELS) as QuestionType[]).map((t) => (
									<MenuItem key={t} value={t}>
										{TYPE_LABELS[t]}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{/* isRequired — not applicable to CONSENT */}
						{form.type !== 'CONSENT' && (
							<FormGroup>
								<FormControlLabel
									control={
										<Checkbox
											checked={form.isRequired}
											onChange={(e: ChangeEvent<HTMLInputElement>) => handleFormChange('isRequired', e.target.checked)}
										/>
									}
									label="Required"
								/>
							</FormGroup>
						)}

						{/* MULTIPLE_CHOICE fields */}
						{form.type === 'MULTIPLE_CHOICE' && (
							<>
								<FormControl>
									<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
										Selection Type
									</Typography>
									<RadioGroup
										row
										value={form.selectionType}
										onChange={(e) => handleFormChange('selectionType', e.target.value)}
									>
										<FormControlLabel value="SINGLE" control={<Radio size="small" />} label="Single" />
										<FormControlLabel value="MULTI" control={<Radio size="small" />} label="Multiple" />
									</RadioGroup>
								</FormControl>

								<Box>
									<Typography variant="caption" color="text.secondary">
										Options
									</Typography>
									<Stack spacing={1} mt={0.5}>
										{form.options.map((opt, i) => (
											<Stack key={i} direction="row" spacing={1} alignItems="center">
												<TextField
													size="small"
													placeholder={`Option ${i + 1}`}
													value={opt.label}
													onChange={(e) => handleOptionChange(i, 'label', e.target.value)}
													sx={{ flexGrow: 1 }}
												/>
												<Tooltip title="Exclusive (selecting this deselects others)">
													<FormControlLabel
														control={
															<Checkbox
																size="small"
																checked={opt.exclusive}
																onChange={(e: ChangeEvent<HTMLInputElement>) =>
																	handleOptionChange(i, 'exclusive', e.target.checked)
																}
															/>
														}
														label={<Typography variant="caption">Excl.</Typography>}
														sx={{ mr: 0 }}
													/>
												</Tooltip>
												<IconButton
													size="small"
													color="error"
													onClick={() => removeOption(i)}
													disabled={form.options.length === 1}
												>
													<Iconify icon="eva:trash-2-outline" width={18} />
												</IconButton>
											</Stack>
										))}
									</Stack>
									<Button size="small" startIcon={<AddIcon />} onClick={addOption} sx={{ mt: 1 }}>
										Add Option
									</Button>
								</Box>
							</>
						)}

						{/* CONSENT field */}
						{form.type === 'CONSENT' && (
							<TextField
								label="Consent Content (HTML)"
								fullWidth
								multiline
								minRows={4}
								value={form.consentContent}
								onChange={(e) => handleFormChange('consentContent', e.target.value)}
								helperText="Enter the full consent text. HTML tags are supported."
							/>
						)}
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={handleDialogClose} color="inherit">
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveQuestion}
						disabled={isSavingQuestion}
						startIcon={isSavingQuestion ? <CircularProgress size={16} color="inherit" /> : null}
					>
						{editingQuestion ? 'Save' : 'Add'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
